// Client/src/components/Sidebar/RSAAnalysis/PathsPanels/LrsRoutePanel.jsx
// "Add route by road / milepost" — creates a route from the state LRS instead
// of two map clicks. START and END points may be on different roads; END
// mirrors START's road until the user changes it. Resolves both measures to
// lat/lng waypoints and hands them to the existing path pipeline (addPath +
// updatePath; the map's PathMapContent effect auto-routes via Google).
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useRsaPathsContext } from "./RsaPathsContext";
import { makeUniqueLabel } from "./useRsaPaths";
import RoutePointPicker from "./RoutePointPicker";
import {
  getLrsAdapter,
  DEFAULT_LRS_STATE,
} from "../../../../utils/stateLrs/registry";
import {
  isMeasureInRanges,
  formatRanges,
} from "../../../../utils/stateLrs/lrsMath";
import "./LrsRoutePanel.css";

const abbrevFor = (network) => (network === "milepoint" ? "MPT" : "MP");
const wordFor = (network) =>
  network === "milepoint" ? "milepoint" : "milepost";

/** Normalize a stored lrs binding (v1 flat or v2 two-point) to start/end points. */
function lrsToPoints(lrs) {
  if (!lrs) return null;
  if (lrs.v === 2) {
    if (!lrs.start?.routeKey || !lrs.end?.routeKey) return null;
    return { start: lrs.start, end: lrs.end, sameRoad: Boolean(lrs.sameRoad) };
  }
  if (!lrs.routeKey) return null;
  const pt = {
    routeKey: lrs.routeKey,
    routeLabel: lrs.routeLabel,
    network: lrs.network,
    zone: null,
  };
  return {
    start: { ...pt, measure: lrs.mpStart },
    end: { ...pt, measure: lrs.mpEnd },
    sameRoad: true,
  };
}

/** Rebuild a picker RouteEntry from a stored lrs point (key encodes the rest). */
function entryFromLrsPoint(pt) {
  if (!pt?.routeKey) return null;
  const [keyNetwork, rest] = String(pt.routeKey).split(":");
  const parts = (rest ?? "").split("-");
  return {
    key: pt.routeKey,
    network: pt.network ?? keyNetwork,
    label: pt.routeLabel ?? pt.routeKey,
    routeType: parts[0] ?? null,
    routeNumber: Number(parts[1]),
    routeDirection: Number(parts[2]),
    countyCode: parts[3] ?? null,
    zone: pt.zone ?? null,
    mRanges: null,
  };
}

/** The auto-generated label for an lrs binding — used to detect user renames. */
function lrsAutoLabel(lrs) {
  const pts = lrsToPoints(lrs);
  if (
    !pts ||
    !Number.isFinite(Number(pts.start.measure)) ||
    !Number.isFinite(Number(pts.end.measure))
  ) {
    return null;
  }
  const a = abbrevFor(pts.start.network);
  const b = abbrevFor(pts.end.network);
  const s = Number(pts.start.measure).toFixed(1);
  const e = Number(pts.end.measure).toFixed(1);
  return pts.sameRoad
    ? `${pts.start.routeLabel} ${a} ${s}-${e}`
    : `${pts.start.routeLabel} ${a} ${s} → ${pts.end.routeLabel} ${b} ${e}`;
}

export default function LrsRoutePanel({
  disabled = false,
  editPath = null,
  onEditPathConsumed,
  onOpenChange,
}) {
  const ctx = useRsaPathsContext();
  const adapter = getLrsAdapter(DEFAULT_LRS_STATE);

  const [open, setOpen] = useState(false);
  const [statewide, setStatewide] = useState(null); // RouteEntry[] | null
  const [loadError, setLoadError] = useState(null);
  const [includeLocal, setIncludeLocal] = useState(false);

  // Per-point selection state. END mirrors START until the user touches END.
  const [startRoute, setStartRoute] = useState(null);
  const [startGeometry, setStartGeometry] = useState(null);
  const [startGeometryError, setStartGeometryError] = useState(null);
  const [endRoute, setEndRoute] = useState(null);
  const [endGeometry, setEndGeometry] = useState(null);
  const [endGeometryError, setEndGeometryError] = useState(null);
  const [endTouched, setEndTouched] = useState(false);

  const [mpStart, setMpStart] = useState("");
  const [endMode, setEndMode] = useState("mp"); // 'mp' | 'len' (same-road only)
  const [mpEnd, setMpEnd] = useState("");
  const [lengthMi, setLengthMi] = useState("");
  const [formError, setFormError] = useState(null);
  const [creating, setCreating] = useState(false);
  // Synchronous guard: `creating` state updates are async, so a double-click
  // could fire two path creations without this ref.
  const creatingRef = useRef(false);
  // Edit mode: when set, submitting updates this existing path in place
  // instead of creating a new one.
  const [editingPathId, setEditingPathId] = useState(null);
  const [editingPathLabel, setEditingPathLabel] = useState("");
  const bodyRef = useRef(null);
  // Per-slot sequence numbers guard against a stale geometry fetch resolving
  // after a newer selection for that slot (e.g. START's mirror-fired END
  // fetch resolving after the user has since picked a different END road).
  const geomSeqRef = useRef({ start: 0, end: 0 });

  // Let the parent declutter around the panel (hide path rows etc.) while
  // the user is mid-flow in here.
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const sameRoad = Boolean(
    startRoute && endRoute && startRoute.key === endRoute.key,
  );
  const startWord = wordFor(startRoute?.network);
  const endWord = wordFor(endRoute?.network);

  const loadStatewide = useCallback(() => {
    setLoadError(null);
    adapter
      .listStatewideRoutes()
      .then(setStatewide)
      .catch((err) => setLoadError(err?.message ?? "Alabama LRS unavailable"));
  }, [adapter]);

  useEffect(() => {
    if (open && !statewide && !loadError) loadStatewide();
  }, [open, statewide, loadError, loadStatewide]);

  const clearMeasures = () => {
    setMpStart("");
    setMpEnd("");
    setLengthMi("");
    setFormError(null);
  };

  const loadGeometry = (route, which) => {
    const seq = ++geomSeqRef.current[which];
    const setGeom = which === "start" ? setStartGeometry : setEndGeometry;
    const setGeomError =
      which === "start" ? setStartGeometryError : setEndGeometryError;
    setGeom(null);
    setGeomError(null);
    adapter
      .getRouteGeometry(route.key)
      .then((g) => {
        if (geomSeqRef.current[which] === seq) setGeom(g);
      })
      .catch((err) => {
        if (geomSeqRef.current[which] === seq) {
          setGeomError(err?.message ?? "Could not load route geometry");
        }
      });
  };

  /** Invalidate any in-flight geometry fetch for a slot (clear/reset paths). */
  const cancelGeometry = (which) => {
    geomSeqRef.current[which] += 1;
  };

  const handleSelectStart = (route) => {
    setStartRoute(route);
    loadGeometry(route, "start");
    clearMeasures();
    if (!endTouched) {
      // Mirror: END follows START until the user explicitly changes END.
      setEndRoute(route);
      loadGeometry(route, "end");
    }
  };

  const handleClearStart = () => {
    setStartRoute(null);
    setStartGeometry(null);
    setStartGeometryError(null);
    cancelGeometry("start");
    clearMeasures();
    if (!endTouched) {
      setEndRoute(null);
      setEndGeometry(null);
      setEndGeometryError(null);
      cancelGeometry("end");
    }
  };

  const handleSelectEnd = (route) => {
    setEndTouched(true);
    setEndRoute(route);
    loadGeometry(route, "end");
    setMpEnd("");
    setLengthMi("");
    setFormError(null);
    if (!startRoute || route.key !== startRoute.key) setEndMode("mp");
  };

  const handleClearEnd = () => {
    setEndTouched(true);
    setEndRoute(null);
    setEndGeometry(null);
    setEndGeometryError(null);
    cancelGeometry("end");
    setMpEnd("");
    setLengthMi("");
    setFormError(null);
  };

  const resetPanel = () => {
    setStartRoute(null);
    setStartGeometry(null);
    setStartGeometryError(null);
    setEndRoute(null);
    setEndGeometry(null);
    setEndGeometryError(null);
    cancelGeometry("start");
    cancelGeometry("end");
    setEndTouched(false);
    setEndMode("mp");
    clearMeasures();
  };

  const exitEditMode = () => {
    setEditingPathId(null);
    setEditingPathLabel("");
    resetPanel();
  };

  const handleCancelCreate = () => {
    resetPanel();
    setOpen(false);
  };

  // Consume an edit request from the path list: reopen prefilled with the
  // path's stored road/milepost selection, switching submit to update-in-place.
  useEffect(() => {
    if (!editPath?.lrs) return;
    const pts = lrsToPoints(editPath.lrs);
    const startEntry = pts && entryFromLrsPoint(pts.start);
    const endEntry = pts && entryFromLrsPoint(pts.end);
    onEditPathConsumed?.();
    if (!startEntry || !endEntry) return;
    setOpen(true);
    setEditingPathId(editPath.id);
    setEditingPathLabel(editPath.label ?? "");
    setStartRoute(startEntry);
    loadGeometry(startEntry, "start");
    setEndRoute(endEntry);
    loadGeometry(endEntry, "end");
    // Cross-road selections must not re-mirror when START changes.
    setEndTouched(!pts.sameRoad);
    setEndMode("mp");
    setMpStart(String(pts.start.measure ?? ""));
    setMpEnd(String(pts.end.measure ?? ""));
    setLengthMi("");
    setFormError(null);
    requestAnimationFrame(() => {
      bodyRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPath]);

  const handleCreate = async () => {
    if (creatingRef.current) return;
    if (!startRoute || !startGeometry || !endRoute || !endGeometry || creating)
      return;
    const start = Number(mpStart);
    const end =
      sameRoad && endMode === "len" ? start + Number(lengthMi) : Number(mpEnd);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      setFormError("Enter numeric values.");
      return;
    }
    if (sameRoad && end <= start) {
      setFormError(
        `${abbrevFor(endRoute.network)} end must be greater than the start.`,
      );
      return;
    }
    if (!isMeasureInRanges(startGeometry.mRanges, start)) {
      setFormError(
        `Start is outside the valid ${startWord} range (${formatRanges(startGeometry.mRanges)}).`,
      );
      return;
    }
    if (!isMeasureInRanges(endGeometry.mRanges, end)) {
      setFormError(
        `End is outside the valid ${endWord} range (${formatRanges(endGeometry.mRanges)}).`,
      );
      return;
    }
    creatingRef.current = true;
    setCreating(true);
    setFormError(null);
    try {
      const [ptA, ptB] = await Promise.all([
        adapter.measureToPoint(startRoute.key, start),
        adapter.measureToPoint(endRoute.key, end),
      ]);
      if (!ptA || !ptB) {
        setFormError("Could not locate a point on its route (measure gap).");
        return;
      }
      const baseLabel = sameRoad
        ? `${startRoute.label} ${abbrevFor(startRoute.network)} ${start.toFixed(1)}-${end.toFixed(1)}`
        : `${startRoute.label} ${abbrevFor(startRoute.network)} ${start.toFixed(1)} → ${endRoute.label} ${abbrevFor(endRoute.network)} ${end.toFixed(1)}`;
      const newLrs = {
        v: 2,
        state: adapter.stateCode,
        start: {
          routeKey: startRoute.key,
          routeLabel: startRoute.label,
          network: startRoute.network,
          measure: start,
          zone: startRoute.zone ?? null,
        },
        end: {
          routeKey: endRoute.key,
          routeLabel: endRoute.label,
          network: endRoute.network,
          measure: end,
          zone: endRoute.zone ?? null,
        },
        sameRoad,
      };
      if (editingPathId) {
        // Update-in-place: replace the selection on the existing path. New
        // waypoints re-route via the map effect, so activate the path first.
        const target = ctx.paths.find((p) => p.id === editingPathId);
        const updates = { waypoints: [ptA, ptB], lrs: newLrs };
        // Regenerate the auto label unless the user renamed the path
        // (startsWith absorbs makeUniqueLabel's _2 suffixes).
        const oldAuto = target?.lrs ? lrsAutoLabel(target.lrs) : null;
        if (!target?.label || (oldAuto && target.label.startsWith(oldAuto))) {
          updates.label = makeUniqueLabel(baseLabel, ctx.paths, editingPathId);
        }
        ctx.actions.setActivePath(editingPathId);
        ctx.actions.updatePath(editingPathId, updates);
        exitEditMode();
      } else {
        const id = ctx.actions.addPath();
        ctx.actions.updatePath(id, {
          label: makeUniqueLabel(baseLabel, ctx.paths),
          waypoints: [ptA, ptB],
          lrs: newLrs,
        });
        resetPanel();
      }
      setOpen(false);
    } catch (err) {
      setFormError(err?.message ?? "Failed to save route.");
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  if (!adapter) return null; // no LRS for this state — feature hidden

  const sourceInfo = adapter.getSourceInfo();

  return (
    <div className="lrs-route-panel">
      <button
        type="button"
        className="paths-panel-add-segment-btn lrs-route-panel-toggle"
        onClick={() => {
          if (open) {
            if (editingPathId) exitEditMode();
            setOpen(false);
          } else {
            setOpen(true);
          }
        }}
        disabled={disabled}
        title={
          disabled
            ? "Finish creating ortho maps for your current route before adding another"
            : `Create a route from ${adapter.stateName} road + milepost data`
        }
      >
        <i className="fas fa-road" aria-hidden /> Add route by road / milepost
      </button>

      {open && (
        <div
          className={`lrs-route-panel-body${!startRoute ? " lrs-route-panel-body--short" : ""}`}
          ref={bodyRef}
        >
          {!startRoute && (
            <button
              type="button"
              className="lrs-route-panel-close"
              onClick={handleCancelCreate}
              aria-label="Cancel route creation"
              title="Cancel"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
          {editingPathId && (
            <div className="lrs-route-panel-editing" role="status">
              <span>
                <i className="fas fa-pen" aria-hidden /> Editing{" "}
                <strong>{editingPathLabel || "route"}</strong> — updating
                replaces its road/milepost selection.
              </span>
              <button
                type="button"
                onClick={() => {
                  // Back to where the user was before clicking edit: panel
                  // closed, route list visible, nothing half-configured.
                  exitEditMode();
                  setOpen(false);
                }}
              >
                Cancel Edit
              </button>
            </div>
          )}
          <div className="lrs-route-panel-header">
            <div className="lrs-route-panel-state">{adapter.stateName}</div>
            {!loadError && (
              <label className="lrs-route-panel-local-toggle">
                <input
                  type="checkbox"
                  checked={includeLocal}
                  onChange={(e) => setIncludeLocal(e.target.checked)}
                  aria-label="Include local roads"
                />
                Include local roads
              </label>
            )}
          </div>

          {loadError && (
            <div className="lrs-route-panel-error" role="alert">
              <span>
                Alabama LRS unavailable — you can still create routes by
                clicking the map.
              </span>
              <button type="button" onClick={loadStatewide}>
                Retry
              </button>
            </div>
          )}

          {sourceInfo.statewideSource === "snapshot" && (
            <div className="lrs-route-panel-notice">
              Road list from LRS snapshot of{" "}
              {String(sourceInfo.snapshotDate).slice(0, 10)}
            </div>
          )}

          {!loadError && (
            <>
              <div
                className={`lrs-route-stops${startRoute ? " lrs-route-stops--linked" : ""}`}
              >
                <div className="lrs-route-stop">
                  <div className="lrs-route-stop-marker" aria-hidden="true">
                    <span className="lrs-route-stop-dot lrs-route-stop-dot--start" />
                  </div>
                  <section className="lrs-point-section">
                    <div className="lrs-point-section-title">Start point</div>
                    <RoutePointPicker
                      adapter={adapter}
                      statewide={statewide}
                      includeLocal={includeLocal}
                      selectedRoute={startRoute}
                      geometry={startGeometry}
                      geometryError={startGeometryError}
                      onSelectRoute={handleSelectStart}
                      onClearRoute={handleClearStart}
                      onRetryGeometry={() => {
                        if (startRoute) loadGeometry(startRoute, "start");
                      }}
                      searchPlaceholder="Search roads (e.g. I-65, US-31, 280)…"
                    />
                    {startRoute && (
                      <label className="lrs-field">
                        {startWord === "milepost"
                          ? "MP start"
                          : "Milepoint start"}
                        <input
                          type="number"
                          step="0.1"
                          value={mpStart}
                          className="lrs-measure-input"
                          onChange={(e) => setMpStart(e.target.value)}
                          aria-label={
                            startWord === "milepost"
                              ? "MP start"
                              : "Milepoint start"
                          }
                        />
                      </label>
                    )}
                  </section>
                </div>

                {/* Step 2 appears once a start road is chosen — one decision
                    at a time (the mirror pre-fills it with the same road). */}
                {startRoute && (
                  <div className="lrs-route-stop">
                    <div className="lrs-route-stop-marker" aria-hidden="true">
                      <span className="lrs-route-stop-dot lrs-route-stop-dot--end" />
                    </div>
                    <section className="lrs-point-section">
                      <div className="lrs-point-section-title">End point</div>
                      <RoutePointPicker
                        adapter={adapter}
                        statewide={statewide}
                        includeLocal={includeLocal}
                        selectedRoute={endRoute}
                        geometry={endGeometry}
                        geometryError={endGeometryError}
                        onSelectRoute={handleSelectEnd}
                        onClearRoute={handleClearEnd}
                        onRetryGeometry={() => {
                          if (endRoute) loadGeometry(endRoute, "end");
                        }}
                        searchPlaceholder="Search end road…"
                        showRange={endTouched && !sameRoad}
                        mirroredNote={
                          sameRoad && endRoute ? "(same as start road)" : null
                        }
                      />

                      {endRoute && sameRoad && (
                        <div
                          className="lrs-route-panel-endmode"
                          role="radiogroup"
                          aria-label="Second point mode"
                        >
                          <label>
                            <input
                              type="radio"
                              name="lrs-end-mode"
                              checked={endMode === "mp"}
                              onChange={() => setEndMode("mp")}
                            />{" "}
                            {endWord === "milepost"
                              ? "End at milepost"
                              : "End at milepoint"}
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="lrs-end-mode"
                              checked={endMode === "len"}
                              onChange={() => setEndMode("len")}
                              aria-label="Length"
                            />{" "}
                            Length
                          </label>
                        </div>
                      )}

                      {endRoute && sameRoad && endMode === "len" ? (
                        <label className="lrs-field">
                          Length (miles)
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={lengthMi}
                            className="lrs-measure-input"
                            onChange={(e) => setLengthMi(e.target.value)}
                            aria-label="Length (miles)"
                          />
                        </label>
                      ) : endRoute ? (
                        <label className="lrs-field">
                          {endWord === "milepost" ? "MP end" : "Milepoint end"}
                          <input
                            type="number"
                            step="0.1"
                            value={mpEnd}
                            className="lrs-measure-input"
                            onChange={(e) => setMpEnd(e.target.value)}
                            aria-label={
                              endWord === "milepost"
                                ? "MP end"
                                : "Milepoint end"
                            }
                          />
                        </label>
                      ) : null}
                    </section>
                  </div>
                )}
              </div>

              {formError && (
                <div className="lrs-route-panel-error" role="alert">
                  {formError}
                </div>
              )}

              {/* Final step: the action appears once both points have roads. */}
              {startRoute && endRoute && (
                <div className="lrs-route-panel-actions">
                  {!editingPathId && (
                    <button
                      type="button"
                      className="lrs-route-panel-cancel"
                      onClick={handleCancelCreate}
                      disabled={creating}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="lrs-route-panel-create"
                    onClick={handleCreate}
                    disabled={!startGeometry || !endGeometry || creating}
                  >
                    {creating
                      ? editingPathId
                        ? "Updating…"
                        : "Creating…"
                      : editingPathId
                        ? "Update Route"
                        : "Create Route"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
